/**
 * 移动端国际化性能优化器
 * 确保国际化功能在移动设备上的良好性能
 * 优化内存使用和渲染性能
 */

class MobileOptimizer {
  constructor() {
    // 设备信息
    this.deviceInfo = this.detectDevice()
    
    // 性能配置
    this.config = {
      // 低端设备配置
      lowEnd: {
        maxCacheSize: 200,
        batchSize: 5,
        debounceDelay: 300,
        memoryLimit: 2 * 1024 * 1024 // 2MB
      },
      // 中端设备配置
      midRange: {
        maxCacheSize: 500,
        batchSize: 10,
        debounceDelay: 200,
        memoryLimit: 3 * 1024 * 1024 // 3MB
      },
      // 高端设备配置
      highEnd: {
        maxCacheSize: 1000,
        batchSize: 20,
        debounceDelay: 100,
        memoryLimit: 5 * 1024 * 1024 // 5MB
      }
    }
    
    // 当前配置
    this.currentConfig = this.getOptimalConfig()
    
    // 性能监控
    this.performanceMetrics = {
      translationTime: [],
      memoryUsage: [],
      renderTime: [],
      lastOptimization: Date.now()
    }
    
    // 防抖函数缓存
    this.debouncedFunctions = new Map()
    
    // 初始化优化
    this.initializeOptimizations()
  }

  /**
   * 检测设备信息
   * @returns {object} 设备信息
   */
  detectDevice() {
    const userAgent = navigator.userAgent.toLowerCase()
    const platform = navigator.platform.toLowerCase()
    
    // 检测设备类型
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent)
    const isIOS = /iphone|ipad|ipod/i.test(userAgent)
    const isAndroid = /android/i.test(userAgent)
    
    // 检测性能等级
    const hardwareConcurrency = navigator.hardwareConcurrency || 2
    const deviceMemory = navigator.deviceMemory || 2
    
    let performanceLevel = 'midRange'
    if (deviceMemory <= 2 || hardwareConcurrency <= 2) {
      performanceLevel = 'lowEnd'
    } else if (deviceMemory >= 4 && hardwareConcurrency >= 4) {
      performanceLevel = 'highEnd'
    }
    
    return {
      isMobile,
      isTablet,
      isIOS,
      isAndroid,
      performanceLevel,
      hardwareConcurrency,
      deviceMemory,
      userAgent,
      platform
    }
  }

  /**
   * 获取最优配置
   * @returns {object} 配置对象
   */
  getOptimalConfig() {
    return this.config[this.deviceInfo.performanceLevel]
  }

  /**
   * 初始化优化
   */
  initializeOptimizations() {
    // 设置内存监控
    this.setupMemoryMonitoring()
    
    // 设置渲染优化
    this.setupRenderOptimization()
    
    // 设置网络优化
    this.setupNetworkOptimization()
    
    console.log('移动端优化初始化完成:', {
      deviceType: this.deviceInfo.isMobile ? 'mobile' : 'desktop',
      performanceLevel: this.deviceInfo.performanceLevel,
      config: this.currentConfig
    })
  }

  /**
   * 设置内存监控
   */
  setupMemoryMonitoring() {
    if (typeof window !== 'undefined' && window.performance && window.performance.memory) {
      // 定期检查内存使用
      setInterval(() => {
        const memoryInfo = window.performance.memory
        this.performanceMetrics.memoryUsage.push({
          used: memoryInfo.usedJSHeapSize,
          total: memoryInfo.totalJSHeapSize,
          limit: memoryInfo.jsHeapSizeLimit,
          timestamp: Date.now()
        })
        
        // 保持最近100个记录
        if (this.performanceMetrics.memoryUsage.length > 100) {
          this.performanceMetrics.memoryUsage.shift()
        }
        
        // 内存使用过高时触发优化
        if (memoryInfo.usedJSHeapSize > this.currentConfig.memoryLimit) {
          this.triggerMemoryOptimization()
        }
      }, 10000) // 每10秒检查一次
    }
  }

  /**
   * 设置渲染优化
   */
  setupRenderOptimization() {
    // 使用 requestIdleCallback 进行空闲时优化
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      const optimizeInIdle = () => {
        window.requestIdleCallback((deadline) => {
          while (deadline.timeRemaining() > 0) {
            this.performIdleOptimization()
            break
          }
          
          // 继续下一轮优化
          setTimeout(optimizeInIdle, 5000)
        })
      }
      
      optimizeInIdle()
    }
  }

  /**
   * 设置网络优化
   */
  setupNetworkOptimization() {
    // 监听网络状态变化
    if (typeof navigator !== 'undefined' && navigator.connection) {
      const connection = navigator.connection
      
      const updateNetworkOptimization = () => {
        const effectiveType = connection.effectiveType
        
        // 根据网络状况调整配置
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          this.currentConfig.batchSize = Math.max(2, this.currentConfig.batchSize / 2)
          this.currentConfig.debounceDelay *= 2
        } else if (effectiveType === '4g') {
          this.currentConfig = this.getOptimalConfig()
        }
      }
      
      connection.addEventListener('change', updateNetworkOptimization)
      updateNetworkOptimization()
    }
  }

  /**
   * 触发内存优化
   */
  triggerMemoryOptimization() {
    console.log('触发内存优化')
    
    // 清理性能指标历史数据
    this.performanceMetrics.translationTime = this.performanceMetrics.translationTime.slice(-20)
    this.performanceMetrics.renderTime = this.performanceMetrics.renderTime.slice(-20)
    
    // 清理防抖函数缓存
    this.debouncedFunctions.clear()
    
    // 触发垃圾回收（如果可用）
    if (typeof window !== 'undefined' && window.gc) {
      window.gc()
    }
    
    this.performanceMetrics.lastOptimization = Date.now()
  }

  /**
   * 空闲时优化
   */
  performIdleOptimization() {
    // 清理过期的性能数据
    const now = Date.now()
    const maxAge = 5 * 60 * 1000 // 5分钟
    
    this.performanceMetrics.translationTime = this.performanceMetrics.translationTime
      .filter(item => now - item.timestamp < maxAge)
    
    this.performanceMetrics.renderTime = this.performanceMetrics.renderTime
      .filter(item => now - item.timestamp < maxAge)
  }

  /**
   * 创建防抖函数
   * @param {Function} func - 原函数
   * @param {number} delay - 延迟时间
   * @param {string} key - 缓存键
   * @returns {Function} 防抖函数
   */
  createDebouncedFunction(func, delay = null, key = null) {
    const actualDelay = delay || this.currentConfig.debounceDelay
    const cacheKey = key || func.toString()
    
    if (this.debouncedFunctions.has(cacheKey)) {
      return this.debouncedFunctions.get(cacheKey)
    }
    
    let timeoutId
    const debouncedFunc = (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func.apply(this, args), actualDelay)
    }
    
    this.debouncedFunctions.set(cacheKey, debouncedFunc)
    return debouncedFunc
  }

  /**
   * 优化翻译函数
   * @param {Function} originalTranslateFunc - 原始翻译函数
   * @returns {Function} 优化后的翻译函数
   */
  optimizeTranslateFunction(originalTranslateFunc) {
    return (key, values) => {
      const startTime = performance.now()
      
      try {
        const result = originalTranslateFunc(key, values)
        
        // 记录翻译时间
        const translationTime = performance.now() - startTime
        this.performanceMetrics.translationTime.push({
          time: translationTime,
          key,
          timestamp: Date.now()
        })
        
        // 保持最近50个记录
        if (this.performanceMetrics.translationTime.length > 50) {
          this.performanceMetrics.translationTime.shift()
        }
        
        return result
      } catch (error) {
        console.warn('翻译函数执行出错:', error)
        return key
      }
    }
  }

  /**
   * 优化渲染函数
   * @param {Function} renderFunc - 渲染函数
   * @returns {Function} 优化后的渲染函数
   */
  optimizeRenderFunction(renderFunc) {
    // 为移动端创建防抖渲染函数
    return this.createDebouncedFunction((args) => {
      const startTime = performance.now()
      
      try {
        const result = renderFunc(args)
        
        // 记录渲染时间
        const renderTime = performance.now() - startTime
        this.performanceMetrics.renderTime.push({
          time: renderTime,
          timestamp: Date.now()
        })
        
        // 保持最近30个记录
        if (this.performanceMetrics.renderTime.length > 30) {
          this.performanceMetrics.renderTime.shift()
        }
        
        return result
      } catch (error) {
        console.warn('渲染函数执行出错:', error)
        return null
      }
    }, this.currentConfig.debounceDelay, 'render')
  }

  /**
   * 批量处理翻译
   * @param {Array} translationTasks - 翻译任务数组
   * @param {Function} translateFunc - 翻译函数
   * @returns {Promise<Array>} 翻译结果数组
   */
  async batchTranslate(translationTasks, translateFunc) {
    const results = []
    const batchSize = this.currentConfig.batchSize
    
    for (let i = 0; i < translationTasks.length; i += batchSize) {
      const batch = translationTasks.slice(i, i + batchSize)
      
      // 处理当前批次
      const batchResults = batch.map(task => {
        try {
          return translateFunc(task.key, task.values)
        } catch (error) {
          console.warn('批量翻译出错:', error)
          return task.key
        }
      })
      
      results.push(...batchResults)
      
      // 在批次之间让出控制权（移动端优化）
      if (i + batchSize < translationTasks.length) {
        await new Promise(resolve => setTimeout(resolve, 0))
      }
    }
    
    return results
  }

  /**
   * 获取性能统计
   * @returns {object} 性能统计信息
   */
  getPerformanceStats() {
    const translationTimes = this.performanceMetrics.translationTime.map(item => item.time)
    const renderTimes = this.performanceMetrics.renderTime.map(item => item.time)
    
    return {
      device: this.deviceInfo,
      config: this.currentConfig,
      translation: {
        count: translationTimes.length,
        avgTime: translationTimes.length > 0 ? 
          translationTimes.reduce((a, b) => a + b, 0) / translationTimes.length : 0,
        maxTime: translationTimes.length > 0 ? Math.max(...translationTimes) : 0
      },
      render: {
        count: renderTimes.length,
        avgTime: renderTimes.length > 0 ? 
          renderTimes.reduce((a, b) => a + b, 0) / renderTimes.length : 0,
        maxTime: renderTimes.length > 0 ? Math.max(...renderTimes) : 0
      },
      memory: {
        current: this.performanceMetrics.memoryUsage.length > 0 ? 
          this.performanceMetrics.memoryUsage[this.performanceMetrics.memoryUsage.length - 1] : null,
        history: this.performanceMetrics.memoryUsage.slice(-10)
      },
      lastOptimization: this.performanceMetrics.lastOptimization
    }
  }

  /**
   * 检查是否需要优化
   * @returns {boolean} 是否需要优化
   */
  shouldOptimize() {
    const now = Date.now()
    const timeSinceLastOptimization = now - this.performanceMetrics.lastOptimization
    
    // 每5分钟至少优化一次
    if (timeSinceLastOptimization > 5 * 60 * 1000) {
      return true
    }
    
    // 检查翻译性能
    const recentTranslations = this.performanceMetrics.translationTime.slice(-10)
    if (recentTranslations.length >= 10) {
      const avgTime = recentTranslations.reduce((a, b) => a.time + b.time, 0) / recentTranslations.length
      if (avgTime > 10) { // 平均翻译时间超过10ms
        return true
      }
    }
    
    // 检查内存使用
    if (this.performanceMetrics.memoryUsage.length > 0) {
      const latestMemory = this.performanceMetrics.memoryUsage[this.performanceMetrics.memoryUsage.length - 1]
      if (latestMemory.used > this.currentConfig.memoryLimit * 0.8) {
        return true
      }
    }
    
    return false
  }

  /**
   * 执行优化
   */
  optimize() {
    if (this.shouldOptimize()) {
      this.triggerMemoryOptimization()
      console.log('移动端性能优化执行完成')
    }
  }
}

// 创建全局移动端优化器实例
const mobileOptimizer = new MobileOptimizer()

// 定期检查并执行优化
if (typeof window !== 'undefined') {
  setInterval(() => {
    mobileOptimizer.optimize()
  }, 60000) // 每分钟检查一次
}

export default mobileOptimizer
export { MobileOptimizer }